import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_API_ERROR } from "../../../constants/commonConstants";
import { iApiError } from "../../../types/commonTypes";
import { IPhotoLibraryPhoto, IPhotoLibrarySection } from "../../../interfaces/photoLibrary";
import { getPhotoLibraryPhotosThunk, getPhotoLibrarySectionsThunk } from "./photoLibraryThunk";

export interface IPhotoLibrarySlice {
	apiError: iApiError | null;
	isLoading: boolean;
	isLoadingPhotos: boolean;
	photoLibrarySections: IPhotoLibrarySection[] | [] | null;
	libraryPhotos: IPhotoLibraryPhoto[] | [] | null;
	activeSection: IPhotoLibrarySection | null;
}

const initialState: IPhotoLibrarySlice = {
	photoLibrarySections: null,
	libraryPhotos: null,
	apiError: null,
	isLoading: false,
	activeSection: null,
	isLoadingPhotos: false,
};

const photoLibrarySlice = createSlice({
	name: "photoLibrary",
	initialState,
	reducers: {
		setActiveSection(state, action: PayloadAction<IPhotoLibrarySection | null>) {
			state.activeSection = action.payload;
		},
		setLibraryPhotos(state, action: PayloadAction<IPhotoLibraryPhoto[]>) {
			state.libraryPhotos = action.payload;
		},
		setPhotoLibrarySections(state, action: PayloadAction<IPhotoLibrarySection[]>) {
			state.photoLibrarySections = action.payload;
		},
		resetPhotoLibraryState(state) {
			state.apiError = null;
			state.activeSection = null;
			state.libraryPhotos = null;
			state.photoLibrarySections = null;
			state.isLoading = false;
			state.isLoadingPhotos = false;
		},
	},
	// Extra Reducers
	extraReducers: (builder) => {
		builder.addCase(getPhotoLibrarySectionsThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = state.photoLibrarySections?.length === 0 ? true : false;
		});
		builder.addCase(getPhotoLibrarySectionsThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});
		builder.addCase(getPhotoLibrarySectionsThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			state.apiError = null;
			state.photoLibrarySections = action.payload;
		});

		builder.addCase(getPhotoLibraryPhotosThunk.pending, (state) => {
			state.apiError = null;
			state.isLoadingPhotos = true;
		});
		builder.addCase(getPhotoLibraryPhotosThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoadingPhotos = false;
		});
		builder.addCase(getPhotoLibraryPhotosThunk.fulfilled, (state, action) => {
			state.apiError = null;
			state.libraryPhotos = action.payload;
			state.isLoadingPhotos = false;
		});
	},
});

export const { resetPhotoLibraryState, setActiveSection, setLibraryPhotos, setPhotoLibrarySections } =
	photoLibrarySlice.actions;
export default photoLibrarySlice.reducer;
